import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import WhatsAppForm from '../components/WhatsAppForm';
import GuideSection from '../components/GuideSection';
import {
  Target,
  Globe,
  Shield,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Phone,
  MessageCircle,
  Zap,
  BarChart3,
  Tag,
  PhoneCall,
  Users,
  Euro,
  LineChart,
  Award,
  ShieldCheck,
  Clock,
  Linkedin,
  ExternalLink,
} from 'lucide-react';
import { sectors, images, socialLinks, contactByLanguage } from '../data/mock';

// Google Colors
const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const HomePage = () => {
  const { language, t } = useLanguage();

  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  const openWhatsApp = () => {
    // GTM Conversion Event - WhatsApp Click
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click_whatsapp',
        click_location: 'homepage',
        language: language
      });
    }
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const callPhone = () => {
    // GTM Conversion Event - Call Click
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click_call',
        click_location: 'homepage',
        phone_number: currentContact.phone,
        language: language
      });
    }
    window.location.href = `tel:${currentContact.phone.replace(/\s/g, '')}`;
  };

  const serviceIcons = {
    campaigns: <Target className="w-7 h-7" />,
    leads: <Users className="w-7 h-7" />,
    compliance: <Shield className="w-7 h-7" />,
    web: <Globe className="w-7 h-7" />,
    tracking: <Tag className="w-7 h-7" />,
  };

  const currentSectors = sectors[language] || sectors.es;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-24 lg:pt-28 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/40 to-white" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-100/30 via-green-50/20 to-yellow-50/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-red-50/20 via-yellow-50/10 to-transparent rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left content */}
            <div className="text-left">
              {/* Badge */}
              <div className="inline-flex items-center space-x-3 bg-white shadow-md px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-slate-100">
                <img src={images.googleAdsLogo} alt="Google Ads" className="h-6 w-auto" />
                <span className="text-slate-700">{t.hero.badge}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                {t.hero.title}
              </h1>
              
              <ul className="space-y-3 mb-8 max-w-xl" data-testid="hero-services-list">
                {t.services.items.map((item) => (
                  <li key={item.id} className="flex items-start gap-3 text-base sm:text-lg text-slate-700" data-testid={`hero-service-${item.id}`}>
                    <CheckCircle className="w-5 h-5 mt-1 shrink-0" style={{ color: googleColors.green }} />
                    <span>{item.title}</span>
                  </li>
                ))}
              </ul>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-8">
                <Link to={`/${language}/compliance`} data-testid="hero-compliance-cta">
                  <Button
                    size="lg"
                    className="text-white font-semibold px-8 py-6 text-base shadow-lg hover:shadow-xl transition-all duration-300 group"
                    style={{ backgroundColor: googleColors.red }}
                  >
                    {t.hero.ctaCompliance}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={callPhone}
                  className="border-2 font-semibold px-6 py-6 text-base group hover:bg-green-50"
                  style={{ borderColor: googleColors.green, color: googleColors.green }}
                >
                  <PhoneCall className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                  {t.hero.ctaCall}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={openWhatsApp}
                  className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-6 py-6 text-base group"
                >
                  <MessageCircle className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                  {t.hero.ctaSecondary}
                </Button>
              </div>
            </div>

            {/* Right content - Form */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-100/40 via-green-50/20 to-yellow-50/10 rounded-3xl transform rotate-2" />
              <Card className="relative bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
                <div className="h-1.5 flex">
                  <div className="flex-1" style={{ backgroundColor: googleColors.blue }} />
                  <div className="flex-1" style={{ backgroundColor: googleColors.red }} />
                  <div className="flex-1" style={{ backgroundColor: googleColors.yellow }} />
                  <div className="flex-1" style={{ backgroundColor: googleColors.green }} />
                </div>
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{t.form.title}</h3>
                  <p className="text-slate-500 mb-6">{t.form.subtitle}</p>
                  <WhatsAppForm variant="compact" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* KPIs Section */}
      <section className="py-16 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full" style={{ backgroundColor: googleColors.blue }} />
          <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full" style={{ backgroundColor: googleColors.green }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h2 className="text-2xl font-bold text-white text-center mb-12">{t.kpis.title}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: googleColors.blue }}>
                <Euro className="w-10 h-10 text-white" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{t.kpis.spent.value}</div>
              <div className="text-slate-400 text-sm">{t.kpis.spent.label}</div>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: googleColors.red }}>
                <Phone className="w-10 h-10 text-white" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{t.kpis.calls.value}</div>
              <div className="text-slate-400 text-sm">{t.kpis.calls.label}</div>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: googleColors.yellow }}>
                <TrendingUp className="w-10 h-10 text-slate-800" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{t.kpis.roi.value}</div>
              <div className="text-slate-400 text-sm">{t.kpis.roi.label}</div>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: googleColors.green }}>
                <Clock className="w-10 h-10 text-white" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{t.kpis.since.value}</div>
              <div className="text-slate-400 text-sm">{t.kpis.since.label}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Ads Partner Section */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <img src={images.googleAdsCertified} alt="Google Ads Certified" className="h-16 w-auto" />
            <div className="hidden md:block w-px h-12 bg-slate-200" />
            <div className="flex items-center gap-2">
              <span className="text-slate-600 text-lg font-medium">Google Ads Partner</span>
              <CheckCircle className="w-5 h-5" style={{ color: googleColors.green }} />
            </div>
          </div>
        </div>
      </section>

      <GuideSection location="home" />

      {/* Mission Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                {t.mission.title}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t.mission.text}
              </p>
            </div>
            <div className="relative">
              <img 
                src={images.illustration} 
                alt="Marketing Digital Illustration" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t.services.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.services.items.map((service, index) => {
              const colors = [googleColors.blue, googleColors.red, googleColors.green, googleColors.yellow, googleColors.blue];
              // Map service IDs to page routes
              const serviceLinks = {
                campaigns: `/${language}/servicios`,
                leads: `/${language}/servicios`,
                compliance: `/${language}/compliance`,
                web: `/${language}/creacion-web`,
                tracking: `/${language}/servicios`,
              };
              const linkLabels = {
                es: 'Ver más',
                fr: 'En savoir plus',
                en: 'Learn more',
              };
              return (
                <Card
                  key={service.id}
                  className="group bg-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden"
                >
                  <CardContent className="p-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-500"
                      style={{ backgroundColor: colors[index] }}
                    >
                      {serviceIcons[service.id]}
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: colors[index] }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link 
                      to={serviceLinks[service.id] || `/${language}/servicios`}
                      className="inline-flex items-center text-sm font-semibold transition-colors hover:underline"
                      style={{ color: colors[index] }}
                    >
                      {linkLabels[language] || 'Ver más'}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Link to all services */}
          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              className="text-white px-8 py-6"
              style={{ backgroundColor: googleColors.blue }}
            >
              <Link to={`/${language}/servicios`}>
                {language === 'es' ? 'Ver todos los servicios' : language === 'fr' ? 'Voir tous les services' : 'View all services'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {t.sectors.title}
            </h2>
            <p className="text-slate-600">{t.sectors.subtitle}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {currentSectors.map((sector, index) => {
              const colors = [googleColors.blue, googleColors.red, googleColors.yellow, googleColors.green, googleColors.blue];
              return (
                <span
                  key={index}
                  className="px-6 py-3 bg-white rounded-full text-slate-700 font-medium shadow-sm hover:shadow-md transition-shadow border-l-4"
                  style={{ borderLeftColor: colors[index] }}
                >
                  {sector}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t.process.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t.process.subtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.process.steps.map((step, index) => {
              const colors = [googleColors.blue, googleColors.red, googleColors.yellow, googleColors.green];
              return (
                <div key={index} className="relative group">
                  {index < t.process.steps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-slate-200 to-transparent" />
                  )}
                  
                  <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-500 border border-slate-100 hover:border-slate-200">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-4 group-hover:scale-110 transition-transform duration-300"
                      style={{ backgroundColor: colors[index] }}
                    >
                      {step.number}
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl" data-testid="founder-section">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                {t.founder.title}
              </h2>
              <h3 className="text-2xl font-semibold mb-2" style={{ color: googleColors.blue }}>
                {t.founder.name}
              </h3>
              <p className="text-slate-500 mb-6">
                {t.founder.role} • {t.founder.location}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t.founder.bio}
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                {t.founder.expertise.map((item, index) => {
                  const colors = [googleColors.blue, googleColors.green, googleColors.red];
                  return (
                    <span
                      key={index}
                      className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: colors[index] }}
                    >
                      <Award className="w-4 h-4 mr-2" />
                      {item}
                    </span>
                  );
                })}
              </div>

              {/* LinkedIn Button */}
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-[#0A66C2] text-white font-semibold rounded-lg hover:bg-[#004182] transition-colors shadow-md hover:shadow-lg"
              >
                <Linkedin className="w-5 h-5 mr-2" />
                LinkedIn
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t.trust.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.trust.items.map((item, index) => {
              const icons = [<TrendingUp className="w-8 h-8" />, <LineChart className="w-8 h-8" />, <ShieldCheck className="w-8 h-8" />];
              const colors = [googleColors.blue, googleColors.green, googleColors.red];
              return (
                <div
                  key={index}
                  className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-500 border border-slate-100"
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-white mx-auto mb-6"
                    style={{ backgroundColor: colors[index] }}
                  >
                    {icons[index]}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Callback Section */}
      <section className="py-16 lg:py-20" style={{ background: `linear-gradient(135deg, ${googleColors.blue} 0%, #1a73e8 100%)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t.callback.title}
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            {t.callback.subtitle}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {t.callback.features.map((feature, index) => (
              <span key={index} className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4 mr-2" />
                {feature}
              </span>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={callPhone}
              className="bg-white hover:bg-slate-50 font-semibold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 group"
              style={{ color: googleColors.blue }}
            >
              <PhoneCall className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
              {t.callback.button}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={openWhatsApp}
              className="border-2 border-white/50 text-white hover:bg-white/10 font-semibold px-10 py-6 text-lg backdrop-blur-sm group"
            >
              <MessageCircle className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-28 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-2 mb-6">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.blue }} />
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.red }} />
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.yellow }} />
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.green }} />
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            {t.cta.subtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/contacto`}>
              <Button
                size="lg"
                className="text-white font-semibold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 group"
                style={{ backgroundColor: googleColors.blue }}
              >
                {t.cta.button}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={openWhatsApp}
              className="border-2 text-emerald-400 hover:bg-emerald-500/10 font-semibold px-10 py-6 text-lg group"
              style={{ borderColor: '#10b981' }}
            >
              <MessageCircle className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
